import { LibraryFolderTree } from "./library-tree";
import { createOwnershipLibraryFixture } from "./library-ownership-fixture";
import { LibraryUpload } from "./manuscript-library";
import type { LibraryAdapter } from "./library-adapter";
import { createReviewLibraryFixture } from "./refine-library-fixture";
import {
  createRefineUploadFixture,
  type RefineUploadExample,
} from "./library-refine-upload";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Link2,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Field,
  Select,
  SegmentedControl,
} from "../../design-system";
import { AiModelSelect } from "./llm-model-select";
import { aiModelsFixture } from "./llm-data";
import {
  RefineMarkdown,
  ReviewNotices,
  ReviewSummary,
} from "./manuscript-review";
import {
  guidelineFixture,
  refineFixture,
  refineLabels,
  type RefineAction,
  type RefineActions,
  type RefineJob,
  type RefineMutationResult,
  type RefineStatus,
  type RefineView,
} from "./refine-data";
import { initialFolders } from "./manuscript-library-data";
import type { FileRef } from "./library-adapter";
import "./refine.css";
const fail = (e: unknown) => (e instanceof Error ? e.message : String(e));
function Elapsed({ start }: { start?: string }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = start
    ? Math.max(0, Math.floor((now - Date.parse(start)) / 1000))
    : 0;
  return (
    <span>
      {Math.floor(seconds / 60)}분 {seconds % 60}초 경과 · 보통 1~3분
    </span>
  );
}
export type RefineExample =
  | "linked-review"
  | "linked-running"
  | "linked-rejected"
  | "linked-register"
  | "linked-conflict"
  | "linked-unchanged"
  | "linked-latest-completed"
  | "files"
  | "list"
  | "review"
  | "running"
  | "queued"
  | "failed"
  | "rejected"
  | "registered"
  | "cancelled"
  | "empty"
  | "error"
  | "loading"
  | "parse-error"
  | "readonly"
  | "create"
  | "budget"
  | "no-key"
  | "disabled"
  | "plan"
  | "register"
  | "rerun"
  | "source";
export function RefineWorkbench({
  view,
  actions = {},
  initialCreate = false,
  initialAction,
  libraryProvider,
  uploadExample,
}: {
  libraryProvider?: LibraryAdapter;
  uploadExample?: RefineUploadExample;
  view: RefineView;
  actions?: RefineActions;
  initialCreate?: boolean;
  initialAction?: RefineAction;
}) {
  const [initialAttemptCount] = useState(view.selected?.attempts.length);
  const [create, setCreate] = useState(initialCreate),
    [q, setQ] = useState(""),
    [customer, setCustomer] = useState(""),
    [status, setStatus] = useState("");
  const filtered = actions.query
    ? view.jobs
    : view.jobs.filter(
        (j) =>
          (!q || j.title.includes(q)) &&
          (!customer || j.folderId === customer) &&
          (!status || j.status === status),
      );
  const query = (patch: { q?: string; folderId?: string; status?: string }) =>
    actions.query?.({
      q,
      folderId: customer,
      status,
      offset: 0,
      limit: 30,
      ...patch,
    });
  return (
    <div className="mrf-page" data-refine>
      {view.selected ? (
        <RefineDetail
          key={view.selected.id + ":" + view.selected.attempts.length}
          job={view.selected}
          view={view}
          actions={actions}
          initialAction={
            view.selected.attempts.length === initialAttemptCount
              ? initialAction
              : undefined
          }
        />
      ) : (
        <>
          <div className="mrf-toolbar">
            <div>
              <strong>정형화 작업</strong>
              <span className="mrf-muted">
                {" "}
                원본을 보존하고, 게시 전에 확인합니다
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={actions.reload}
              disabled={!actions.reload}
            >
              <RefreshCw size={15} />
              새로고침
            </Button>
          </div>
          <div className="mrf-filter">
            <Field
              label="작업 검색"
              placeholder="제목 검색"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                query({ q: e.target.value });
              }}
            />
            <Select
              label="대상 폴더"
              value={customer}
              onChange={(e) => {
                setCustomer(e.target.value);
                query({ folderId: e.target.value });
              }}
            >
              <option value="">모든 대상 폴더</option>
              {view.folders.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select
              label="상태"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                query({ status: e.target.value });
              }}
            >
              <option value="">전체 상태</option>
              {Object.entries(refineLabels).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          {view.loading ? (
            <div className="mrf-state" role="status">
              정형화 작업을 불러오고 있습니다…
            </div>
          ) : view.error ? (
            <div className="mrf-state" role="alert">
              {view.error}
              <Button onClick={actions.reload} disabled={!actions.reload}>
                다시 읽기
              </Button>
            </div>
          ) : !filtered.length ? (
            <div className="mrf-state">
              <Sparkles />
              <h3>정형화 작업이 없습니다</h3>
              <p>원본을 올리면 정형화 결과를 검수할 수 있습니다.</p>
            </div>
          ) : (
            <div
              className="mrf-table"
              role="region"
              aria-label="정형화 작업 목록"
              tabIndex={0}
            >
              <table>
                <thead>
                  <tr>
                    {[
                      "제목 · 대상 폴더",
                      "상태",
                      "시도",
                      "최신 결과",
                      "비용",
                      "시각 · 담당자",
                    ].map((s) => (
                      <th key={s}>{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((j) => {
                    const a = j.attempts.at(-1)!;
                    return (
                      <tr key={j.id}>
                        <td>
                          <button
                            className="mrf-link"
                            onClick={() => actions.open?.(j.id)}
                            disabled={!actions.open}
                          >
                            {j.title}
                          </button>
                          <small>
                            대상 폴더:{" "}
                            {view.folders.find((f) => f.id === j.folderId)
                              ?.name ?? "폴더 밖 원고"}{" "}
                            · {j.id}
                          </small>
                        </td>
                        <td>
                          <Badge
                            tone={
                              j.status === "review"
                                ? "amber"
                                : j.status === "failed"
                                  ? "coral"
                                  : j.status === "registered"
                                    ? "teal"
                                    : "blue"
                            }
                          >
                            {refineLabels[j.status]}
                          </Badge>
                        </td>
                        <td>{j.attempts.length}회</td>
                        <td>
                          보류 {a.report.hold.length} · 검토 {a.notices.length}{" "}
                          · 오류 {a.parse.errors.length}
                        </td>
                        <td>
                          {a.provider === "codex" ? (
                            <Badge>플랜 · $0</Badge>
                          ) : (
                            "$" + a.costUsd.toFixed(3)
                          )}
                        </td>
                        <td>
                          {j.createdAt}
                          <small>{j.author}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {actions.query && view.total > view.jobs.length && (
            <Button
              variant="secondary"
              onClick={() =>
                actions.query?.({
                  q,
                  customerId: customer,
                  status,
                  offset: view.jobs.length,
                  limit: 30,
                })
              }
            >
              더 읽기 ({view.jobs.length}/{view.total})
            </Button>
          )}
          {view.canCreate && (
            <Button className="mcc-fab mrf-fab" onClick={() => setCreate(true)}>
              <Plus size={18} />
              정형화로 올리기
            </Button>
          )}
        </>
      )}
      {create && (
        <LibraryUpload
          provider={libraryProvider}
          initialChannel="refine"
          refineExample={uploadExample}
          customerId={
            view.uploadTarget?.customerId ?? view.customers[0]?.id ?? ""
          }
          folderId={view.uploadTarget?.folderId ?? null}
          targets={{ customers: view.customers, folders: view.folders }}
          onClose={() => setCreate(false)}
          refine={{
            guidelines: view.guidelines,
            models: view.models,
            readiness: view.readiness,
            hqCustomerId: view.hqCustomerId,
            canCreate: view.canCreate,
            enqueue: actions.enqueue,
            batch: actions.batch,
            uploadFile: actions.uploadFile,
            onOpenSettings: actions.openSettings,
            onOpenList: (ids) => {
              actions.showCreated?.(ids);
              actions.back?.();
            },
          }}
        />
      )}
    </div>
  );
}
export function RefineDetail({
  job,
  view,
  actions,
  initialAction,
}: {
  job: RefineJob;
  view: RefineView;
  actions: RefineActions;
  initialAction?: RefineAction;
}) {
  const [seq, setSeq] = useState(
      job.status === "review"
        ? (job.registrationAttemptSeq ?? job.attempts.at(-1)!.seq)
        : job.attempts.at(-1)!.seq,
    ),
    [sourceRevision, setSourceRevision] = useState(
      (job.attempts.find((t) => t.seq === seq) ?? job.attempts.at(-1)!)
        .sourceRevision,
    ),
    [mobileTab, setMobileTab] = useState("result"),
    [format, setFormat] = useState("preview"),
    [modal, setModal] = useState<RefineAction | undefined>(initialAction),
    [registrationResult, setRegistrationResult] = useState(
      view.registrationResult,
    );
  const a = job.attempts.find((x) => x.seq === seq) ?? job.attempts.at(-1)!;
  const latest = a.seq === job.attempts.at(-1)!.seq;
  const source =
    job.sources.find((s) => s.revision === sourceRevision) ??
    job.sources.at(-1)!;
  const registrationAttempt =
    job.attempts.find((t) => t.seq === job.registrationAttemptSeq) ??
    [...job.attempts].reverse().find((t) => t.status === "done");
  const allowed = (action: RefineAction) =>
    (action === "register" ? a.seq === registrationAttempt?.seq : latest) &&
    job.allowedActions.includes(action) &&
    !!actions.mutate &&
    !(action === "delete" && job.manuscriptId);
  return (
    <>
      <div className="mrf-toolbar">
        <Button variant="ghost" onClick={actions.back} disabled={!actions.back}>
          <ArrowLeft size={16} />
          정형화 목록
        </Button>
        <span>
          {job.id} · 대상 폴더:{" "}
          {view.folders.find((f) => f.id === job.folderId)?.name ??
            "폴더 밖 원고"}
        </span>
        <Badge tone={job.status === "review" ? "amber" : "blue"}>
          {refineLabels[job.status]}
        </Badge>
      </div>
      <h2 className="mrf-title">{job.title}</h2>
      <nav className="mrf-attempts" aria-label="시도 이력">
        {job.attempts.map((t) => (
          <button
            key={t.seq}
            aria-pressed={seq === t.seq}
            onClick={() => {
              setSeq(t.seq);
              setSourceRevision(t.sourceRevision);
            }}
          >
            <strong>
              #{t.seq} <span>가이드라인 v{t.guideline.version}</span>
            </strong>
            <small>
              {t.model} · {Math.round(t.durationMs / 1000)}초 ·{" "}
              {t.provider === "codex" ? "플랜" : "$" + t.costUsd.toFixed(3)}
            </small>
            <span>
              {t.registeredRevision !== undefined
                ? "등록됨 · v" + t.registeredRevision
                : (t.decision ??
                  (t.status === "done"
                    ? "검수 대기"
                    : (refineLabels[t.status as RefineStatus] ?? t.status)))}
              {t.decisionNote ? " · " + t.decisionNote : ""}
            </span>
          </button>
        ))}
      </nav>
      {!latest && (
        <p className="mrf-warning">
          이전 시도를 보고 있습니다. 반려·재검수·원본 수정은 최신 시도에서
          진행합니다.
          {registrationAttempt?.seq === a.seq &&
            " 이 시도는 등록할 수 있는 가장 최근 완료 결과입니다."}
        </p>
      )}
      {(job.status === "running" || job.status === "queued") && (
        <div className="mrf-progress" role="status">
          <Sparkles size={24} />
          <div>
            <strong>{refineLabels[job.status]}</strong>
            <p>
              {job.status === "running" ? (
                <Elapsed start={a.startedAt} />
              ) : (
                "실행할 차례를 기다리고 있습니다."
              )}
            </p>
            <span className="mrf-progress-line" />
          </div>
        </div>
      )}
      {job.status === "failed" && (
        <p className="mrf-warning" role="alert">
          {a.error ??
            "AI 응답을 완료하지 못했습니다. 모델·원본을 확인하고 재검수해 주세요."}
        </p>
      )}
      {job.status === "rejected" && (
        <p className="mrf-warning">
          반려 사유: {a.decisionNote ?? "반려 사유가 제공되지 않았습니다."}
        </p>
      )}
      {registrationResult && (
        <div className="mrf-success" role="status">
          <ShieldCheck size={20} />
          <strong>
            {registrationResult.revised
              ? registrationResult.revised.changed
                ? "원고 v" +
                  registrationResult.revised.revision +
                  "으로 올렸습니다"
                : "바뀐 내용 없음 · 원고 v" +
                  registrationResult.revised.revision +
                  " 유지"
              : "새 원고로 등록했습니다"}
          </strong>
          <Button
            variant="secondary"
            disabled={!actions.openManuscript}
            onClick={() => {
              const id =
                registrationResult.revised?.manuscriptId ??
                registrationResult.manuscriptId;
              if (id) actions.openManuscript?.(id);
            }}
          >
            원고 보기
          </Button>
        </div>
      )}
      {job.manuscriptId && (
        <section className="mrf-linked" aria-label="연결된 원고">
          <Link2 size={20} />
          <div>
            <strong>
              연결된 원고 {job.linkedManuscript?.title ?? job.manuscriptId}
              {job.linkedManuscript ? " v" + job.linkedManuscript.revision : ""}
            </strong>
            <p>다시 정형화해서 등록하면 이 원고의 새 버전으로 올라갑니다.</p>
            <small>
              연결 중에는 정형화 작업을 삭제할 수 없습니다. 원고함에서 원고를
              삭제하면 연결이 풀립니다.
            </small>
          </div>
          <Button
            variant="secondary"
            onClick={() =>
              job.manuscriptId && actions.openManuscript?.(job.manuscriptId)
            }
            disabled={!actions.openManuscript}
          >
            등록된 원고 보기
          </Button>
        </section>
      )}
      <div className="mrf-mobile-tabs">
        <SegmentedControl
          label="원본 결과 전환"
          value={mobileTab}
          options={[
            { value: "source", label: "원본" },
            { value: "result", label: "결과" },
          ]}
          onChange={setMobileTab}
        />
      </div>
      <div className="mrf-comparison">
        <section
          className={
            "mrf-paper " + (mobileTab !== "source" ? "mobile-hidden" : "")
          }
        >
          <header>
            <strong>
              <FileText size={16} />
              원본
            </strong>
            <Select
              label="원본 버전"
              value={String(sourceRevision)}
              onChange={(e) => setSourceRevision(Number(e.target.value))}
            >
              {job.sources.map((s) => (
                <option key={s.revision} value={s.revision}>
                  원본 v{s.revision}
                </option>
              ))}
            </Select>
          </header>
          <pre className="mrf-lines">
            {source.body.split("\n").map((l, i) => (
              <span key={i}>
                <i>{i + 1}</i>
                {l || " "}
              </span>
            ))}
          </pre>
          <footer>
            {source.files.map((f) => (
              <Badge key={f.fileId}>{f.name}</Badge>
            ))}
          </footer>
        </section>
        <section
          className={
            "mrf-paper " + (mobileTab !== "result" ? "mobile-hidden" : "")
          }
        >
          <header>
            <strong>
              <Sparkles size={16} />
              결과 · 시도 {a.seq}
            </strong>
            <SegmentedControl
              label="결과 표시"
              value={format}
              options={[
                { value: "preview", label: "미리보기" },
                { value: "markdown", label: "마크다운" },
              ]}
              onChange={setFormat}
            />
          </header>
          {a.status === "running" || a.status === "queued" ? (
            <div className="mrf-state">완료되면 결과가 표시됩니다.</div>
          ) : format === "preview" ? (
            <RefineMarkdown body={a.body} />
          ) : (
            <pre className="mrf-lines">
              {a.body.split("\n").map((l, i) => (
                <span key={i}>
                  <i>{i + 1}</i>
                  {l || " "}
                </span>
              ))}
            </pre>
          )}
        </section>
      </div>
      {a.status === "done" && (
        <div className="mrf-reports">
          <section className="mrf-panel">
            <header>
              <h3>
                보고서 <small>AI 작성</small>
              </h3>
              <Badge tone="amber">{a.report.status}</Badge>
            </header>
            <h4>보류 사유</h4>
            {a.report.hold.map((v, i) => (
              <p key={i}>{v}</p>
            ))}
            <details open>
              <summary>형식 충돌 · {a.report.conflicts.length}</summary>
              {a.report.conflicts.map((c, i) => (
                <p key={i}>
                  {c.line}행 · {c.text}
                  <br />
                  {c.effect}
                </p>
              ))}
            </details>
            <details>
              <summary>떼어 낸 지시·메타</summary>
              {a.report.removed.map((r, i) => (
                <p key={i}>
                  {r.text} <ArrowRight size={12} /> {r.movedTo}
                </p>
              ))}
            </details>
            <details>
              <summary>쓰지 않은 파일 · {a.report.unusedFiles.length}</summary>
              {a.report.unusedFiles.join(", ") || "없음"}
            </details>
            <details className="mrf-files-report">
              <summary>
                지원하지 않는 파일 {a.report.unsupportedFiles?.length ?? "—"}
              </summary>
              {a.report.unsupportedFiles?.map((name) => (
                <p key={name}>{name}</p>
              ))}
              <p>지원하지 않는 형식은 원고에 넣지 않습니다.</p>
            </details>
            <details className="mrf-files-report">
              <summary>
                보조 문서 {a.report.auxDocuments?.length ?? "—"}
              </summary>
              {a.report.auxDocuments?.map((doc) => (
                <p key={doc.name}>
                  {doc.name}
                  {doc.chars !== undefined &&
                    ` · ${doc.chars.toLocaleString()}자`}
                  {doc.truncated && " · 일부만 사용"}
                </p>
              ))}
            </details>
            <details>
              <summary>반복 문장</summary>
              {a.report.repeated.map((r, i) => (
                <p key={i}>
                  {r.text} · {r.count}회
                </p>
              ))}
            </details>
            <h4>게시 규모</h4>
            <dl className="mrf-scale">
              {Object.entries({
                사진: a.report.scale.media,
                영상: a.report.scale.videos,
                "링크 카드": a.report.scale.linkCards,
                소제목: a.report.scale.headings,
                문단: a.report.scale.paragraphs,
                "예상 단계": a.report.scale.steps,
              }).map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v ?? "—"}</dd>
                </div>
              ))}
            </dl>
            <details>
              <summary>등록정보</summary>
              <p>키워드: {a.report.registration.keywords.join(", ")}</p>
              <p>일정: {a.report.registration.schedule}</p>
              <p>메모: {a.report.registration.memo}</p>
            </details>
          </section>
          <section className="mrf-panel">
            <header>
              <h3>
                자동 검토 <small>서버 대조</small>
              </h3>
              <Badge tone="amber">검토 {a.notices.length}</Badge>
            </header>
            <ReviewNotices notices={a.notices} />
          </section>
          <section className="mrf-panel">
            <header>
              <h3>
                형식 검사 <small>파서</small>
              </h3>
            </header>
            <ReviewSummary
              errors={a.parse.errors.length}
              warnings={a.parse.warnings.length}
              notices={a.notices.length}
            />
            {a.parse.errors.map((v, i) => (
              <p className="mrf-error" key={i}>
                {v}
              </p>
            ))}
            {a.parse.warnings.map((v, i) => (
              <p key={i}>{v}</p>
            ))}
            <p className="mrf-muted">
              {a.parse.errors.length
                ? "형식 오류를 해결해야 등록할 수 있습니다."
                : "형식 오류가 없습니다. 검토 항목을 확인한 뒤 등록할 수 있습니다."}
            </p>
          </section>
        </div>
      )}
      <div className="mrf-actions">
        {(
          ["source", "delete", "cancel", "reject", "rerun", "register"] as const
        )
          .filter(
            (action) =>
              job.allowedActions.includes(action) &&
              !(action === "delete" && job.manuscriptId),
          )
          .map((action) => (
            <Button
              key={action}
              size={action === "cancel" || action === "delete" ? "sm" : "md"}
              variant={action === "register" ? "primary" : "secondary"}
              disabled={
                !allowed(action) ||
                (action === "delete" && !!job.manuscriptId) ||
                (action === "register" &&
                  (!registrationAttempt || a.parse.errors.length > 0))
              }
              onClick={() => setModal(action)}
            >
              {
                {
                  source: "원본 수정",
                  delete: "삭제",
                  cancel: "정형화 취소",
                  reject: "반려",
                  rerun: "재검수",
                  register: "원고함에 등록",
                }[action]
              }
            </Button>
          ))}
      </div>
      {modal && (
        <RefineActionDialog
          key={modal}
          action={modal}
          job={job}
          view={view}
          actions={actions}
          onRegistered={setRegistrationResult}
          onClose={() => setModal(undefined)}
        />
      )}
    </>
  );
}
function RefineActionDialog({
  action,
  job,
  view,
  actions,
  onClose,
  onRegistered,
}: {
  action: RefineAction;
  job: RefineJob;
  view: RefineView;
  actions: RefineActions;
  onClose: () => void;
  onRegistered: (result: RefineMutationResult) => void;
}) {
  const completed =
    job.attempts.find((t) => t.seq === job.registrationAttemptSeq) ??
    [...job.attempts].reverse().find((t) => t.status === "done");
  const a =
      (action === "register" ? completed : undefined) ?? job.attempts.at(-1)!,
    source = job.sources.at(-1)!;
  const [note, setNote] = useState(""),
    [body, setBody] = useState(source.body),
    [files, setFiles] = useState(source.files),
    [guideline, setGuideline] = useState(a.guideline.id),
    [model, setModel] = useState(
      a.provider === (view.models.billing === "plan" ? "codex" : "openai")
        ? a.model
        : "",
    ),
    [asNew, setAsNew] = useState(false),
    [conflict, setConflict] = useState(false),
    [folder, setFolder] = useState(job.folderId),
    [state, setState] = useState("draft"),
    [keywords, setKeywords] = useState(
      a.report.registration.keywords.join(", "),
    ),
    [rerun, setRerun] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef(false);
  const names = {
    register: "원고함에 등록",
    reject: "반려",
    rerun: "재검수",
    source: "원본 수정",
    cancel: "정형화 취소",
    delete: "정형화 작업 삭제",
  };
  async function submit() {
    if (
      pending.current ||
      !job.allowedActions.includes(action) ||
      !actions.mutate ||
      (action === "delete" && !!job.manuscriptId) ||
      (action === "register" && (!completed || a.parse.errors.length > 0))
    )
      return;
    if (action === "reject" && !note.trim()) {
      setError("반려 사유를 입력해 주세요.");
      return;
    }
    if (action === "source" && !body.trim()) {
      setError("원본 글을 입력해 주세요.");
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!actions.mutate) throw Error("동작이 연결되지 않았습니다.");
      const result = await actions.mutate(
        job.id,
        action,
        action === "register"
          ? job.manuscriptId && !asNew
            ? { asNew: false }
            : {
                ...(job.manuscriptId ? { asNew: true } : {}),
                folderId: folder || null,
                state,
                keywords: keywords
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              }
          : action === "source"
            ? { sourceBody: body, files, note, rerun }
            : action === "rerun"
              ? { note, guidelineId: guideline, model }
              : action === "reject"
                ? { reason: note }
                : {},
      );
      if (action === "register" && result) onRegistered(result);
      onClose();
    } catch (e) {
      setError(fail(e));
      if (action === "register" && job.manuscriptId && !asNew) {
        const status =
          (e as { status?: number; statusCode?: number })?.status ??
          (e as { statusCode?: number })?.statusCode;
        setConflict(status === 409);
      }
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      title={names[action]}
      size={action === "source" ? "wide" : "default"}
      onOpenChange={(v) => !v && !busy && onClose()}
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose, disabled: busy }}
          primary={{
            label: busy
              ? "처리 중…"
              : action === "source" && rerun
                ? "저장하고 바로 재검수"
                : action === "register" && job.manuscriptId
                  ? asNew
                    ? "새 원고로 등록"
                    : "새 버전으로 올리기"
                  : names[action],
            onClick: submit,
            disabled:
              busy ||
              !job.allowedActions.includes(action) ||
              !actions.mutate ||
              (action === "delete" && !!job.manuscriptId) ||
              (action === "register" &&
                (!completed || a.parse.errors.length > 0)),
          }}
        />
      }
    >
      <div className="mrf-form">
        {action === "register" && (
          <>
            <p className="mrf-muted">
              시도 #{a.seq} · 가장 최근에 완료된 결과를 등록합니다.
            </p>
            {job.manuscriptId && (
              <fieldset className="mrf-register-options" disabled={busy}>
                <legend>등록 방식</legend>
                <label data-selected={!asNew}>
                  <input
                    type="radio"
                    name="refine-register-mode"
                    checked={!asNew}
                    onChange={() => setAsNew(false)}
                  />
                  <span>
                    <strong>연결된 원고의 새 버전으로 올리기</strong>
                    <small>
                      {job.linkedManuscript?.title ?? job.manuscriptId}
                      {job.linkedManuscript
                        ? " v" + job.linkedManuscript.revision
                        : ""}{" "}
                      · 지금 폴더와 상태 유지
                    </small>
                    <small>내용이 같으면 버전이 올라가지 않습니다.</small>
                  </span>
                </label>
                <label data-selected={asNew}>
                  <input
                    type="radio"
                    name="refine-register-mode"
                    checked={asNew}
                    onChange={() => setAsNew(true)}
                  />
                  <span>
                    <strong>새 원고로 등록</strong>
                    <small>기존 원고는 그대로 두고 새 원고에 연결합니다.</small>
                  </span>
                </label>
              </fieldset>
            )}
            {(!job.manuscriptId || asNew) && (
              <fieldset className="mrf-register-fields" disabled={busy}>
                {view.folderTreeProvider ? (
                  <fieldset className="mrf-folder-select">
                    <legend>대상 폴더(선택)</legend>
                    <LibraryFolderTree
                      provider={view.folderTreeProvider}
                      customerId={
                        view.folderTreeProvider.defaultScopeId ??
                        view.hqCustomerId ??
                        ""
                      }
                      rootLabel="메타크루 전체"
                      value={folder || "none"}
                      onSelect={(id) =>
                        setFolder(id === "all" || id === "none" ? "" : id)
                      }
                    />
                  </fieldset>
                ) : (
                  <Select
                    label="대상 폴더(선택)"
                    value={folder}
                    onChange={(e) => setFolder(e.target.value)}
                  >
                    <option value="">폴더 밖 원고</option>
                    {view.folders.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </Select>
                )}
                <Select
                  label="원고 상태"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="draft">작성 중</option>
                  <option value="ready">준비됨</option>
                </Select>
                <Field
                  label="SEO 키워드"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </fieldset>
            )}
            <p>
              검토 {a.notices.length}개를 확인했습니다. 자동 검토는 등록을 막지
              않습니다.
            </p>
            {conflict && !asNew && (
              <div className="mrf-warning">
                <p>
                  현재 원고를 새 버전으로 변경할 수 없습니다. 기존 원고를 남기고
                  새 원고로 등록할 수 있습니다.
                </p>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setAsNew(true)}
                >
                  새 원고로 등록 선택
                </Button>
              </div>
            )}
          </>
        )}
        {(action === "reject" || action === "rerun" || action === "source") && (
          <label className="mrf-field">
            {action === "reject" ? "반려 사유 (필수)" : "추가 메모"}
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        )}
        {action === "rerun" && (
          <>
            <Select
              label="재검수 가이드라인"
              value={guideline}
              onChange={(e) => setGuideline(e.target.value)}
            >
              {view.guidelines
                .filter((g) => g.kind === "refine")
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.id === a.guideline.id ? "이전과 같음 · " : ""}v
                    {g.version} {g.label}
                  </option>
                ))}
            </Select>
            <AiModelSelect
              models={view.models}
              value={model}
              onChange={setModel}
              alwaysShow
            />
            <div className="mrf-context">
              <strong>다음 시도에 전달할 컨텍스트</strong>
              <p>
                이전 시도의 반려 사유·보류·자동 검토가 컨텍스트로 들어갑니다.
              </p>
              {a.context.map((s, i) => (
                <p key={i}>{s}</p>
              ))}
            </div>
          </>
        )}
        {action === "source" && (
          <>
            <label className="mrf-field">
              원본 v{source.revision} → v{source.revision + 1}
              <textarea
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
            <div className="mrf-chips">
              {files.map((f) => (
                <Badge key={f.fileId}>
                  {f.name}
                  <button
                    aria-label={`${f.name} 빼기`}
                    onClick={() =>
                      setFiles(files.filter((x) => x.fileId !== f.fileId))
                    }
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <Field
              label="첨부 파일 교체·추가"
              type="file"
              multiple
              accept="image/*,video/*"
              disabled={busy || !actions.uploadFile}
              onChange={async (e) => {
                setBusy(true);
                try {
                  const refs: FileRef[] = [];
                  for (const f of Array.from(e.target.files ?? []))
                    refs.push(await actions.uploadFile!(job.customerId, f));
                  setFiles((v) => [...v, ...refs]);
                } catch (e) {
                  setError(fail(e));
                } finally {
                  setBusy(false);
                }
              }}
            />
            <Checkbox
              checked={rerun}
              onChange={setRerun}
              label="저장하고 바로 재검수"
            />
          </>
        )}
        {(action === "cancel" || action === "delete") && (
          <p>
            {action === "cancel"
              ? "진행 중인 정형화를 취소할까요? 이전 시도 기록은 남습니다."
              : "등록 전 정형화 작업과 시도 기록을 삭제할까요?"}
          </p>
        )}
        {error && (
          <p role="alert" className="mrf-error">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
export function RefinePreview({
  example = "list",
  canWrite = true,
  onOpenSettings,
  onOpenManuscript,
  initialCreate = false,
  initialTarget,
  onRegister,
  uploadExample,
  initialJobs,
  enqueue,
}: {
  enqueue?: import("./library-refine-upload").RefineUploadProvider["enqueue"];
  uploadExample?: RefineUploadExample;
  initialJobs?: RefineJob[];
  example?: RefineExample;
  canWrite?: boolean;
  onOpenSettings?: () => void;
  onOpenManuscript?: (id: string) => void;
  onRegister?: (
    job: RefineJob,
    payload: Record<string, unknown>,
  ) => Promise<string | RefineMutationResult>;
  initialCreate?: boolean;
  initialTarget?: { customerId: string; folderId: string | null };
}) {
  const [jobs, setJobs] = useState(() => {
    if (initialJobs) return initialJobs;
    if (uploadExample === "partial" || uploadExample === "complete")
      return [0, 1, 2]
        .filter((i) => uploadExample === "complete" || i !== 1)
        .map((i) => ({
          ...refineFixture("queued"),
          id: `REF-${100 + i}`,
          title: ["예약", "진료", "상담"][i],
        }));
    const base = refineFixture(
      ([
        "running",
        "queued",
        "failed",
        "rejected",
        "registered",
        "cancelled",
      ].includes(example)
        ? example
        : "review") as RefineStatus,
    );

    if (example.startsWith("linked-")) {
      base.manuscriptId = "ms-1";
      base.linkedManuscript = { title: "진료 예약 안내", revision: 2 };
      base.attempts[0].decision = "등록됨";
      base.attempts[0].registeredRevision = 1;
      base.attempts[1].decision = "등록됨";
      base.attempts[1].registeredRevision = 2;
      base.allowedActions = ["register", "reject", "rerun", "source"];
      if (example === "linked-running") {
        base.status = "running";
        base.attempts.at(-1)!.status = "running";
        base.allowedActions = ["cancel"];
      }
      if (example === "linked-rejected") {
        base.status = "rejected";
        base.allowedActions = ["rerun", "source"];
      }
      if (example === "linked-latest-completed") {
        base.registrationAttemptSeq = 3;
        base.attempts.push({
          ...structuredClone(base.attempts.at(-1)!),
          seq: 4,
          status: "failed",
          body: "",
          error: "실행기 연결이 끊겼습니다.",
          parse: { errors: ["완료되지 않은 결과"], warnings: [] },
        });
      }
    }

    if (example === "files") {
      const a = base.attempts.at(-1)!;
      a.report.unsupportedFiles = ["예약 안내.pdf", "참고 자료.hwp"];
      a.report.auxDocuments = [
        { name: "등록정보.json", chars: 120 },
        { name: "상품 참고.txt", chars: 20000, truncated: true },
      ];
      a.notices = [
        ...a.notices,
        {
          code: "unsupported_file",
          level: "info",
          message:
            "지원하지 않는 형식 2개: 예약 안내.pdf, 참고 자료.hwp — 원고에는 못 넣습니다",
        },
        {
          code: "aux_truncated",
          level: "warn",
          message: "상품 참고.txt는 길이 제한으로 일부만 사용했습니다.",
        },
      ];
    }
    if (example === "parse-error")
      base.attempts.at(-1)!.parse.errors = [
        "12행 · 첨부 목록에 없는 파일 missing.jpg",
      ];
    return example === "empty"
      ? []
      : [
          base,
          { ...refineFixture("queued"), id: "REF-13", title: "주말 방문 안내" },
          {
            ...refineFixture("registered"),
            id: "REF-11",
            title: "예약 변경 안내",
          },
        ];
  });
  const upload =
    ["create", "budget", "no-key", "disabled", "plan"].includes(example) ||
    initialCreate ||
    !!uploadExample;
  const [selected, setSelected] = useState<string | undefined>(
      !!initialJobs ||
        example === "list" ||
        example === "empty" ||
        example === "error" ||
        example === "loading" ||
        upload
        ? undefined
        : "REF-12",
    ),
    [broken, setBroken] = useState(example === "error");
  const [createdIds, setCreatedIds] = useState<string[] | null>(
    initialJobs?.map((j) => j.id) ?? null,
  );
  const [batchProvider] = useState(() =>
    createRefineUploadFixture((newJobs) =>
      setJobs((old) => [...newJobs, ...old]),
    ),
  );
  const [libraryProvider] = useState(createReviewLibraryFixture);
  const [folderTreeProvider] = useState(createOwnershipLibraryFixture);
  const data = jobs.find((j) => j.id === selected);
  const view: RefineView = {
    folderTreeProvider,
    uploadTarget: initialTarget,
    jobs: createdIds ? jobs.filter((j) => createdIds.includes(j.id)) : jobs,
    total: createdIds?.length ?? jobs.length,
    selected: data
      ? {
          ...data,
          allowedActions:
            canWrite && example !== "readonly" ? data.allowedActions : [],
        }
      : undefined,
    loading: example === "loading",
    error: broken ? "정형화 작업을 불러오지 못했습니다." : undefined,
    hqCustomerId: "hq",
    customers: [
      { id: "onyou", name: "온유의원" },
      { id: "moru", name: "모루식당" },
    ],
    folders: initialFolders.map((f) => ({
      id: f.id,
      customerId: f.customerId,
      name: f.name,
    })),
    guidelines: guidelineFixture,
    models:
      example === "plan"
        ? {
            default: "plan-model",
            allowed: ["plan-model", "plan-small"],
            billing: "plan",
          }
        : aiModelsFixture,
    canCreate: canWrite && example !== "readonly",
    readiness:
      example === "budget"
        ? {
            reason: "오늘 정형화 예산을 초과했습니다.",
            usedUsd: 20.4,
            limitUsd: 20,
          }
        : example === "no-key"
          ? { reason: "사용할 API 키가 없습니다." }
          : example === "disabled"
            ? { reason: "AI 중계가 꺼져 있습니다." }
            : undefined,
  };
  return (
    <RefineWorkbench
      view={view}
      libraryProvider={libraryProvider}
      uploadExample={uploadExample}
      initialCreate={upload}
      initialAction={
        [
          "register",
          "rerun",
          "source",
          "linked-register",
          "linked-conflict",
          "linked-unchanged",
        ].includes(example)
          ? example.startsWith("linked-")
            ? "register"
            : (example as RefineAction)
          : undefined
      }
      actions={{
        enqueue,
        batch: batchProvider.batch,
        showCreated: setCreatedIds,
        open: setSelected,
        back: () => setSelected(undefined),
        reload: () => setBroken(false),
        openSettings: onOpenSettings,
        openManuscript: onOpenManuscript,
        uploadFile: async (_, file) => ({
          fileId: "preview-" + crypto.randomUUID(),
          name: file.name,
        }),
        create: async (input) => {
          const j = refineFixture("queued");
          j.id = "REF-" + Date.now();
          j.title = input.title || "새 정형화 작업";
          j.customerId = input.customerId;
          j.customer = view.customers.find(
            (c) => c.id === input.customerId,
          )!.name;
          j.sources = [
            { revision: 1, body: input.sourceBody, files: input.files },
          ];
          j.attempts = [
            {
              ...j.attempts[0],
              seq: 1,
              sourceRevision: 1,
              status: "queued",
              provider: view.models.billing === "plan" ? "codex" : "openai",
              model: input.model || view.models.default,
            },
          ];
          setJobs((v) => [j, ...v]);
          setSelected(j.id);
        },
        mutate: async (id, action, payload) => {
          await new Promise((r) => setTimeout(r, 250));
          const job = jobs.find((j) => j.id === id)!;
          if (
            action === "register" &&
            example === "linked-conflict" &&
            !payload.asNew
          )
            throw Object.assign(
              new Error(
                "게시되었거나 발행 예정인 원고입니다. 새 원고로 등록하세요.",
              ),
              { status: 409 },
            );
          const registration =
            action === "register"
              ? example === "linked-unchanged" && !payload.asNew
                ? {
                    revised: {
                      manuscriptId: job.manuscriptId!,
                      revision: job.linkedManuscript!.revision,
                      changed: false,
                    },
                  }
                : onRegister
                  ? await onRegister(job, payload)
                  : job.manuscriptId && !payload.asNew
                    ? {
                        revised: {
                          manuscriptId: job.manuscriptId,
                          revision: (job.linkedManuscript?.revision ?? 1) + 1,
                          changed: true,
                        },
                      }
                    : { manuscriptId: "LIB-REF12" }
              : undefined;
          const result =
            typeof registration === "string"
              ? { manuscriptId: registration }
              : registration;
          const registeredId =
            result?.revised?.manuscriptId ?? result?.manuscriptId;
          if (action === "delete") {
            setJobs((v) => v.filter((j) => j.id !== id));
            setSelected(undefined);
            return;
          }
          setJobs((v) =>
            v.map((j) => {
              if (j.id !== id) return j;
              const next = structuredClone(j);
              if (action === "register") {
                next.status = "registered";
                next.manuscriptId = registeredId;
                next.linkedManuscript = {
                  title: next.title,
                  revision: result?.revised?.revision ?? 1,
                };
                const attempt = [...next.attempts]
                  .reverse()
                  .find((t) => t.status === "done");
                if (attempt) {
                  attempt.decision = "등록됨";
                  attempt.registeredRevision = next.linkedManuscript.revision;
                }
                next.allowedActions = ["rerun", "source"];
              }
              if (action === "reject") {
                next.status = "rejected";
                next.allowedActions = next.manuscriptId
                  ? ["rerun", "source"]
                  : ["rerun", "source", "delete"];
                next.attempts.at(-1)!.decisionNote = String(payload.reason);
              }
              if (action === "cancel") {
                next.status = next.manuscriptId ? "registered" : "cancelled";
                next.attempts.at(-1)!.status = "cancelled";
                next.allowedActions = next.manuscriptId
                  ? ["rerun", "source"]
                  : ["rerun", "source", "delete"];
              }
              if (action === "source")
                next.sources.push({
                  revision: next.sources.length + 1,
                  body: String(payload.sourceBody),
                  files: payload.files as FileRef[],
                });
              if (
                action === "rerun" ||
                (action === "source" && payload.rerun)
              ) {
                next.status = "queued";
                next.attempts.push({
                  ...structuredClone(next.attempts.at(-1)!),
                  seq: next.attempts.length + 1,
                  sourceRevision: next.sources.length,
                  status: "queued",
                  decision: undefined,
                  registeredRevision: undefined,
                  decisionNote: undefined,
                  context: [
                    ...next.attempts.at(-1)!.context,
                    String(payload.note ?? ""),
                  ],
                  guideline: view.guidelines.find(
                    (g) => g.id === payload.guidelineId,
                  )
                    ? {
                        id: String(payload.guidelineId),
                        version: view.guidelines.find(
                          (g) => g.id === payload.guidelineId,
                        )!.version,
                        label: "",
                      }
                    : next.attempts.at(-1)!.guideline,
                });
                next.allowedActions = next.manuscriptId
                  ? ["cancel"]
                  : ["cancel", "delete"];
              }
              return next;
            }),
          );
          if (action === "register" && !job.manuscriptId && registeredId)
            onOpenManuscript?.(registeredId);
          return result;
        },
      }}
    />
  );
}
